import { css, SerializedStyles } from '@emotion/react';
import React from 'react';
import { ColorPalette } from './ColorPalette';
import { useLongPress } from './mobile_ui/MButton';
import { MEDIA_QUERY_MOBILE } from './mobile_ui/MStyles';

export interface BaseCardViewProps {
  onMouseEnter?: React.EventHandler<React.MouseEvent>;
  onMouseLeave?: React.EventHandler<React.MouseEvent>;
  onLeftClick?: React.EventHandler<React.MouseEvent>;
  onRightClick?: React.EventHandler<React.MouseEvent>;

  cardStyle?: React.CSSProperties;

  // rendered under the border
  underlayStyle?: React.CSSProperties;

  overlayStyle?: React.CSSProperties;
  overlayCSS?: SerializedStyles;
  overlayChildren?: React.ReactNode;

  className?: string;

  landscape?: boolean;
  mini?: boolean;
}
const BaseCardView = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<BaseCardViewProps>
>((props, ref) => {
  const {
    cardStyle,
    underlayStyle,
    overlayStyle,
    overlayCSS,
    overlayChildren,
    className,
    onLeftClick,
    onRightClick,
    onMouseEnter,
    onMouseLeave,
  } = props;

  const longPressProps = useLongPress({
    onShortPress: (e) => {
      onLeftClick && onLeftClick(e as any);
    },
    onLongPress: (e) => {
      onMouseEnter && onMouseEnter(e as any);
    },
    onLongPressEnd: (e) => {
      onMouseLeave && onMouseLeave(e as any);
    },
    ignoreMovementTouches: true,
  });

  const sizeCSS = props.mini
    ? CardStyle.container_mini
    : props.landscape
      ? CardStyle.container_landscape
      : CardStyle.container_portrait;

  return (
    <div
      className={'base-card ' + (props.mini ? 'mini ' : '') + (className || '')}
      css={[CardStyle.container, sizeCSS]}
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onContextMenu={onRightClick}
      {...longPressProps}
    >
      <div css={CardStyle.clippedContainer}>
        {underlayStyle && (
          <div css={CardStyle.underlay} style={underlayStyle} />
        )}
        <div css={CardStyle.card} style={cardStyle}>
          {props.children}
        </div>
      </div>
      {(overlayChildren || overlayStyle || overlayCSS) && (
        <div
          css={overlayCSS ? [CardStyle.overlay, overlayCSS] : CardStyle.overlay}
          style={overlayStyle}
        >
          {overlayChildren}
        </div>
      )}
    </div>
  );
});
export default BaseCardView;

export const BaseCardBoxShadow = 'inset 0px -15px 30px -15px #000000';
export const CARD_BORDER_RADIUS = 5;
export const CARD_BORDER_WIDTH = 3.5;
export const CARD_BLEED_WIDTH = 5;
const CardStyle = {
  container: css({
    position: 'relative',
  }),
  container_portrait: css({
    // 2.48in x 3.48in standard poker size
    width: 100,
    height: '139.51612903225px', //'1.453293in',
  }),
  container_landscape: css({
    width: '139.51612903225px',
    height: 100,
  }),
  clippedContainer: css({
    display: 'inline-flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    width: '100%',
    borderRadius: CARD_BORDER_RADIUS,
  }),
  // 1.61 x 2.48 mini size
  container_mini: css({
    width: 100,
    height: 100,
  }),
  underlay: css({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  }),
  card: css({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',

    border: `solid ${ColorPalette.printBlack} ${CARD_BORDER_WIDTH}px`,
    borderRadius: CARD_BORDER_RADIUS,

    flexGrow: 1,

    MozUserSelect: 'none',
    WebkitUserSelect: 'none',
    msUserSelect: 'none',

    backgroundSize: '100% 100%',
  }),
  body: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',

    flexGrow: 1,

    [MEDIA_QUERY_MOBILE]: {
      backgroundPosition: 'center',
    },
  }),
  overlay: css({
    position: 'absolute',

    top: 0,
    bottom: 0,
    left: 0,
    right: 0,

    borderRadius: CARD_BORDER_RADIUS,
  }),
} as const;
